import React, { useCallback, useEffect, useRef } from "react";
import { ManagerContext } from "../../App";
import {
  Button,
  Container,
  Typography,
  TextField,
  Box,
  Alert,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoginIcon from "@mui/icons-material/Login";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AutomationSettingsPanel from "../../components/AutomationSettings/AutomationSettingsPanel";
import ManagerApi, { GraphAuthStatusInfo } from "../../api/ManagerApi";
import {
  extractOAuthCodeFromUrl,
  openMicrosoftGraphOAuthPopup,
  watchOAuthPopupForCode,
  isMicrosoftGraphOAuthMessage,
} from "../../utils/microsoftGraphOAuth";

import './ManagerSettingsView.scss';

type StatusPresentation = {
  label: string;
  chipColor: 'success' | 'warning' | 'error' | 'default';
  alertSeverity: 'success' | 'warning' | 'error' | 'info';
  icon: React.ReactNode;
  borderColor: string;
};

const getStatusPresentation = (status: GraphAuthStatusInfo['status']): StatusPresentation => {
  switch (status) {
    case 'connected':
      return {
        label: 'Connected',
        chipColor: 'success',
        alertSeverity: 'success',
        icon: <CheckCircleOutlineIcon color="success" />,
        borderColor: '#2e7d32',
      };
    case 'expiring_soon':
      return {
        label: 'Expiring soon',
        chipColor: 'warning',
        alertSeverity: 'warning',
        icon: <WarningAmberIcon color="warning" />,
        borderColor: '#ed6c02',
      };
    case 'expired':
      return {
        label: 'Token expired',
        chipColor: 'warning',
        alertSeverity: 'warning',
        icon: <WarningAmberIcon color="warning" />,
        borderColor: '#ed6c02',
      };
    case 'reauth_required':
      return {
        label: 'Re-authorization required',
        chipColor: 'error',
        alertSeverity: 'error',
        icon: <ErrorOutlineIcon color="error" />,
        borderColor: '#d32f2f',
      };
    case 'config_error':
      return {
        label: 'Configuration error',
        chipColor: 'error',
        alertSeverity: 'error',
        icon: <ErrorOutlineIcon color="error" />,
        borderColor: '#d32f2f',
      };
    case 'not_connected':
    default:
      return {
        label: 'Not connected',
        chipColor: 'default',
        alertSeverity: 'info',
        icon: <CloudOffIcon color="disabled" />,
        borderColor: '#9e9e9e',
      };
  }
};

const formatDateTime = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const ManagerSettingsView = () => {
  const manager = React.useContext(ManagerContext);

  const [redirectUrl, setRedirectUrl] = React.useState<string>('');
  const [uploadResult, setUploadResult] = React.useState<{ successful: number; failed: number; errors: string[] } | null>(null);
  const [isUploading, setIsUploading] = React.useState<boolean>(false);
  const [graphAuthStatus, setGraphAuthStatus] = React.useState<GraphAuthStatusInfo | null>(null);
  const [isLoadingGraphStatus, setIsLoadingGraphStatus] = React.useState<boolean>(true);
  const [graphStatusError, setGraphStatusError] = React.useState<string>('');
  const [tokenSetupMessage, setTokenSetupMessage] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSettingToken, setIsSettingToken] = React.useState<boolean>(false);
  const [authInProgress, setAuthInProgress] = React.useState<boolean>(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = React.useState<boolean>(false);
  const [isDisconnecting, setIsDisconnecting] = React.useState<boolean>(false);
  const oauthPopupRef = useRef<Window | null>(null);
  const stopPopupWatchRef = useRef<(() => void) | null>(null);
  const oauthHandledRef = useRef(false);

  const loadGraphAuthStatus = useCallback(async () => {
    setIsLoadingGraphStatus(true);
    setGraphStatusError('');
    try {
      const status = await ManagerApi.getGraphAuthStatus();
      setGraphAuthStatus(status);
    } catch (error) {
      console.error('Error loading Graph auth status:', error);
      setGraphStatusError('Unable to load Microsoft Graph status. Please try again.');
      setGraphAuthStatus(null);
    } finally {
      setIsLoadingGraphStatus(false);
    }
  }, []);

  useEffect(() => {
    loadGraphAuthStatus();
  }, [loadGraphAuthStatus]);

  const completeOAuthCode = useCallback(async (code: string) => {
    if (oauthHandledRef.current) {
      return;
    }
    oauthHandledRef.current = true;

    setIsSettingToken(true);
    setTokenSetupMessage(null);
    try {
      await ManagerApi.setEmailToken(code);
      setTokenSetupMessage({ type: 'success', text: 'Microsoft Graph authorization saved successfully.' });
      setRedirectUrl('');
      await loadGraphAuthStatus();
    } catch (error) {
      console.error('Error setting email token:', error);
      oauthHandledRef.current = false;
      setTokenSetupMessage({
        type: 'error',
        text: 'Failed to save authorization. The code may have expired — try signing in again.',
      });
    } finally {
      setIsSettingToken(false);
      setAuthInProgress(false);
      oauthPopupRef.current = null;
      stopPopupWatchRef.current?.();
      stopPopupWatchRef.current = null;
    }
  }, [loadGraphAuthStatus]);

  useEffect(() => {
    return () => {
      stopPopupWatchRef.current?.();
    };
  }, []);

  /*
   * Receive the authorization code from the OAuth popup.
   *
   * The callback page posts { type, code, error } to its opener and then closes itself immediately.
   * Nothing was ever subscribed to that message, so every completed sign-in was silently discarded —
   * which is why no token had ever been stored in production despite the sign-in succeeding.
   *
   * watchOAuthPopupForCode cannot cover this on its own: it polls every 350ms, and the popup has
   * usually closed before the first tick, so it reports "closed" rather than a result. The two are
   * complementary — this handles the normal path, the poller handles a callback page that cannot
   * reach its opener.
   */
  useEffect(() => {
    const handleOAuthMessage = (event: MessageEvent) => {
      // Same-origin only: the callback page posts with window.location.origin as the target.
      if (event.origin !== window.location.origin) return;
      if (!isMicrosoftGraphOAuthMessage(event.data)) return;

      stopPopupWatchRef.current?.();
      stopPopupWatchRef.current = null;
      oauthPopupRef.current = null;

      if (event.data.error) {
        oauthHandledRef.current = false;
        setAuthInProgress(false);
        setTokenSetupMessage({ type: 'error', text: `Sign-in failed: ${event.data.error}` });
        return;
      }

      if (event.data.code) {
        void completeOAuthCode(event.data.code);
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [completeOAuthCode]);

  const handleEmailTokenSetup = async () => {
    try {
      setTokenSetupMessage(null);
      oauthHandledRef.current = false;
      const url = await ManagerApi.getEmailTokenUrl();
      const popup = openMicrosoftGraphOAuthPopup(url);

      if (!popup) {
        setTokenSetupMessage({
          type: 'error',
          text: 'Popup was blocked. Allow popups for this site, or use the manual option below.',
        });
        return;
      }

      oauthPopupRef.current = popup;
      setAuthInProgress(true);
      stopPopupWatchRef.current?.();
      stopPopupWatchRef.current = watchOAuthPopupForCode(popup, {
        onResult: (result) => {
          oauthPopupRef.current = null;
          if (result.error) {
            oauthHandledRef.current = false;
            setAuthInProgress(false);
            setTokenSetupMessage({
              type: 'error',
              text: `Microsoft sign-in failed: ${result.error}`,
            });
            return;
          }
          if (result.code) {
            completeOAuthCode(result.code);
          }
        },
        onClosed: () => {
          if (oauthPopupRef.current === popup && !oauthHandledRef.current) {
            oauthPopupRef.current = null;
            setAuthInProgress(false);
            // Never close silently. A popup that flashes and vanishes with no message is
            // indistinguishable from success, and that is exactly how several completed sign-ins
            // were mistaken for the button doing nothing.
            setTokenSetupMessage({
              type: 'error',
              text:
                'The sign-in window closed before the authorization came back. If you did complete sign-in, ' +
                'copy the full URL from that window and use "Paste redirect URL manually" below.',
            });
          }
        },
      });
    } catch (error) {
      console.error('Error getting email token URL:', error);
      setTokenSetupMessage({ type: 'error', text: 'Failed to start Microsoft sign-in. Please try again.' });
      setAuthInProgress(false);
    }
  };

  const handleSetEmailToken = async () => {
    const code = extractOAuthCodeFromUrl(redirectUrl);
    if (!code) {
      setTokenSetupMessage({
        type: 'error',
        text: 'No authorization code found in the URL. Paste the full redirect URL from your browser after signing in.',
      });
      return;
    }

    await completeOAuthCode(code);
  };

  const canDisconnectGraph = Boolean(
    graphAuthStatus &&
      graphAuthStatus.status !== 'not_connected' &&
      graphAuthStatus.status !== 'config_error',
  );

  const handleDisconnectGraph = async () => {
    setIsDisconnecting(true);
    setTokenSetupMessage(null);
    try {
      const status = await ManagerApi.clearGraphAuth();
      setGraphAuthStatus(status);
      setDisconnectDialogOpen(false);
      setTokenSetupMessage({
        type: 'success',
        text: 'Microsoft Graph disconnected. Email features will not work until you sign in again.',
      });
    } catch (error) {
      console.error('Error disconnecting Graph auth:', error);
      setTokenSetupMessage({
        type: 'error',
        text: 'Failed to disconnect Microsoft Graph. Please try again.',
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setIsUploading(true);
    setUploadResult(null);

    try {
      const result = await ManagerApi.uploadClientsCSV(file);
      setUploadResult(result);
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert(`Failed to upload CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const statusPresentation = graphAuthStatus
    ? getStatusPresentation(graphAuthStatus.status)
    : getStatusPresentation('not_connected');

  return (
    <>
      <div className="manager-settings">
        <Container className="manager-settings-container">
          <Typography variant="h1" className="manager-settings-title">
            Manager Settings
          </Typography>

          <AutomationSettingsPanel />

          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Account Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Manager Name
                </Typography>
                <Typography variant="body1">
                  {manager?.Manager_FirstName} {manager?.Manager_LastName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {manager?.Manager_Email}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={2} sx={{ p: 3, mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <EmailOutlinedIcon color="primary" />
                <Box>
                  <Typography variant="h5">
                    Microsoft Graph Email
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email authorization for the firm's Outlook account — LOR drafts, case email ingestion,
                    and automated notifications all depend on this connection.
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Refresh status">
                <span>
                  <IconButton
                    onClick={loadGraphAuthStatus}
                    disabled={isLoadingGraphStatus}
                    aria-label="Refresh Microsoft Graph status"
                    size="small"
                  >
                    {isLoadingGraphStatus ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Paper
              variant="outlined"
              className="graph-auth-status-card"
              sx={{
                p: 2.5,
                mb: 3,
                borderLeft: 4,
                borderLeftColor: statusPresentation.borderColor,
                backgroundColor: 'grey.50',
              }}
            >
              {graphStatusError ? (
                <Alert severity="error" sx={{ mb: 0 }}>
                  {graphStatusError}
                </Alert>
              ) : isLoadingGraphStatus && !graphAuthStatus ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" color="text.secondary">
                    Checking Microsoft Graph connection…
                  </Typography>
                </Box>
              ) : graphAuthStatus ? (
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {statusPresentation.icon}
                      <Chip
                        label={statusPresentation.label}
                        color={statusPresentation.chipColor}
                        size="small"
                        variant={graphAuthStatus.connected ? 'filled' : 'outlined'}
                      />
                      {graphAuthStatus.graphVerified && (
                        <Chip label="Verified with Microsoft" size="small" variant="outlined" color="success" />
                      )}
                    </Box>
                  </Box>

                  <Alert severity={statusPresentation.alertSeverity} sx={{ py: 0.5 }}>
                    {graphAuthStatus.message}
                  </Alert>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Authorized account
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {graphAuthStatus.accountName || graphAuthStatus.accountEmail || '—'}
                      </Typography>
                      {graphAuthStatus.accountName && graphAuthStatus.accountEmail && (
                        <Typography variant="caption" color="text.secondary">
                          {graphAuthStatus.accountEmail}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last authorized
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(graphAuthStatus.lastAuthorizedAt)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Auto-refresh
                      </Typography>
                      <Typography variant="body2">
                        {graphAuthStatus.canAutoRefresh ? 'Available' : 'Not available — re-authorize required'}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              ) : null}
            </Paper>

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Authorize or re-authorize
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sign in with the Microsoft account that should send and receive case email. A sign-in window will open — complete sign-in there and this page will update automatically.
            </Typography>

            {tokenSetupMessage && (
              <Alert severity={tokenSetupMessage.type} sx={{ mb: 2 }} onClose={() => setTokenSetupMessage(null)}>
                {tokenSetupMessage.text}
              </Alert>
            )}

            {authInProgress && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
                Waiting for Microsoft sign-in… Complete sign-in in the popup window.
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={authInProgress || isSettingToken ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
                onClick={handleEmailTokenSetup}
                disabled={authInProgress || isSettingToken || isDisconnecting}
              >
                {authInProgress || isSettingToken
                  ? 'Signing in…'
                  : graphAuthStatus?.connected
                    ? 'Re-authorize with Microsoft'
                    : 'Sign in with Microsoft'}
              </Button>

              {canDisconnectGraph && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={isDisconnecting ? <CircularProgress size={18} color="inherit" /> : <LinkOffIcon />}
                  onClick={() => setDisconnectDialogOpen(true)}
                  disabled={authInProgress || isSettingToken || isDisconnecting}
                >
                  {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                </Button>
              )}
            </Box>

            <Dialog
              open={disconnectDialogOpen}
              onClose={() => !isDisconnecting && setDisconnectDialogOpen(false)}
            >
              <DialogTitle>Disconnect Microsoft Graph?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  This removes the stored Outlook authorization from the server. LOR drafts, email ingestion,
                  and other Graph features will stop working until someone signs in again.
                  {graphAuthStatus?.accountEmail && (
                    <>
                      <br /><br />
                      Currently connected as <strong>{graphAuthStatus.accountEmail}</strong>.
                    </>
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDisconnectDialogOpen(false)} disabled={isDisconnecting}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDisconnectGraph}
                  color="error"
                  variant="contained"
                  disabled={isDisconnecting}
                >
                  {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                </Button>
              </DialogActions>
            </Dialog>

            <Accordion
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="text.secondary">
                  Having trouble? Paste redirect URL manually
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  If the popup does not work, open Microsoft sign-in in a new tab, then paste the full URL from your browser address bar after sign-in.
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="https://…?code=…"
                    value={redirectUrl}
                    onChange={(e) => setRedirectUrl(e.target.value)}
                    sx={{ flex: 1 }}
                    fullWidth
                    disabled={isSettingToken}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSetEmailToken}
                    disabled={!redirectUrl.trim() || isSettingToken}
                    sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                  >
                    {isSettingToken ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Paper>

          <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Import Users from CSV
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Upload a CSV file containing client and ticket data to bulk import users into the system.
            </Typography>

            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
              <Button
                variant="contained"
                component="label"
                disabled={isUploading}
                color="success"
              >
                {isUploading ? 'Uploading...' : 'Select CSV File'}
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleCSVUpload}
                />
              </Button>
              <Typography variant="body2" color="textSecondary">
                Upload a CSV file with client and ticket data
              </Typography>
            </Box>

            {uploadResult && (
              <Box sx={{ mt: 2 }}>
                <Alert
                  severity={uploadResult.failed === 0 ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  Upload completed: {uploadResult.successful} successful, {uploadResult.failed} failed
                </Alert>
                {uploadResult.errors.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      Errors:
                    </Typography>
                    <Box sx={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      p: 2,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'error.main'
                    }}>
                      {uploadResult.errors.map((error, index) => (
                        <Typography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                          • {error}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Paper>

        </Container>
      </div>
    </>
  );
};

export default ManagerSettingsView;
